import { apiClient } from "@wtf/shared/api/utils/api-client";

export interface PlatformSubscriptionDto {
  id: string;
  plan: "FREE" | "PRO" | "MAX";
  status: "ACTIVE" | "TRIALING" | "PAST_DUE" | "CANCELED" | "INCOMPLETE" | "INCOMPLETE_EXPIRED" | "UNPAID";
  tryOnsUsed: number;
  tryOnsLimit: number;
  tryOnsRemaining: number;
  // Primary period fields (single source of truth for ALL users)
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  // Legacy fields - kept for backwards compatibility during migration
  billingPeriodStart: string;
  billingPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  scheduledPlan: "FREE" | "PRO" | "MAX" | null;
  createdAt: string;
  updatedAt: string;
}

export interface UsageStatsDto {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
  // Primary period fields (single source of truth)
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  // Legacy fields - kept for backwards compatibility
  billingPeriodStart: string;
  billingPeriodEnd: string | null;
}

export interface CreateCheckoutSessionResponse {
  url: string; // Stripe hosted checkout URL
  sessionId: string;
}

export interface ChangePlanResponse {
  message: string;
  url?: string; // For upgrades - redirect to checkout
  sessionId?: string; // For upgrades - checkout session ID
  requiresCheckout?: boolean; // True if redirect needed
  immediate?: boolean; // For downgrades - whether change is immediate
  effectiveDate?: string; // For downgrades - when change takes effect
  plan?: string;
  tryOnsLimit?: number;
}

export interface SessionStatusResponse {
  status: string;
  customerEmail?: string;
  plan?: string;
  subscriptionStatus?: string;
}

export interface PlanPricing {
  plan: "FREE" | "PRO" | "MAX";
  amount: number;
  currency: string;
  display: string;
  tryOnsLimit: number;
  stripePriceId?: string;
}

export interface PricingResponse {
  free: PlanPricing;
  pro: PlanPricing;
  max: PlanPricing;
}

export interface InvoiceDto {
  id: string;
  date: string | null;
  amount: number;
  currency: string;
  status: string; // 'draft', 'open', 'paid', 'uncollectible', 'void'
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  number: string | null;
}

export interface BillingHistoryResponse {
  invoices: InvoiceDto[];
}

const platformSubscriptionsApi = {
  /**
   * Get current user's platform subscription
   */
  async getCurrentSubscription(): Promise<PlatformSubscriptionDto> {
    return apiClient<PlatformSubscriptionDto>("api/platform-subscriptions/current", "GET");
  },

  // Setup intent removed - using hosted checkout for all payments

  /**
   * Create Stripe hosted checkout session for subscription
   * Returns URL to redirect user to Stripe's payment page
   */
  async createCheckoutSession(
    plan: "PRO" | "MAX",
    successUrl: string,
    cancelUrl: string
  ): Promise<CreateCheckoutSessionResponse> {
    return apiClient<CreateCheckoutSessionResponse>("api/platform-subscriptions/create-checkout-session", "POST", {
      plan,
      successUrl,
      cancelUrl,
    });
  },

  /**
   * Get checkout session status
   */
  async getSessionStatus(sessionId: string): Promise<SessionStatusResponse> {
    return apiClient<SessionStatusResponse>(`api/platform-subscriptions/session-status?session_id=${sessionId}`, "GET");
  },

  /**
   * Get try-on usage statistics
   */
  async getUsage(): Promise<UsageStatsDto> {
    return apiClient<UsageStatsDto>("api/platform-subscriptions/usage", "GET");
  },

  /**
   * Change subscription plan (unified endpoint)
   * For FREE → PRO/MAX upgrades: Returns checkout URL (requires successUrl/cancelUrl)
   * For PRO → MAX upgrades: Immediate upgrade via API (no redirect)
   * For downgrades: Schedules change at period end
   */
  async changePlan(
    targetPlan: "FREE" | "PRO" | "MAX",
    successUrl?: string,
    cancelUrl?: string
  ): Promise<ChangePlanResponse> {
    return apiClient<ChangePlanResponse>("api/platform-subscriptions/change-plan", "POST", {
      targetPlan,
      successUrl,
      cancelUrl,
    });
  },

  /**
   * Get pricing for all plans (directly from Stripe)
   */
  async getPricing(): Promise<PricingResponse> {
    return apiClient<PricingResponse>("api/platform-subscriptions/pricing", "GET");
  },

  /**
   * Reactivate a scheduled cancellation/downgrade
   */
  async reactivateSubscription(): Promise<{ message: string }> {
    return apiClient<{ message: string }>("api/platform-subscriptions/reactivate", "POST");
  },

  /**
   * Get billing history (invoices)
   */
  async getBillingHistory(): Promise<BillingHistoryResponse> {
    return apiClient<BillingHistoryResponse>("api/platform-subscriptions/billing-history", "GET");
  },

  /**
   * Preview subscription upgrade with proration calculation
   */
  async previewUpgrade(targetPlan: "PRO" | "MAX"): Promise<{
    proratedAmount: number;
    nextBillingAmount: number;
    currency: string;
    currentPlan: string;
    targetPlan: string;
    newTryOnsLimit: number;
  }> {
    return apiClient("api/platform-subscriptions/preview-upgrade", "POST", { targetPlan });
  },
};

export default platformSubscriptionsApi;
